import { CurrencyCode, TaxType, TransactionStatus } from "../API";
import { TransactionFull } from "../models";
import { getUserName, stringWithoutManualLineBreaks } from "../utils";

export type PreparedExpenseReceipt = {
  id: string;
  key?: string;
};

export type PreparedExpenseTransaction = {
  id: string;
  paidAt: string;
  title: string;
  employeeName: string;
  originalAmount: number;
  currencyCode: CurrencyCode;
  baseAmount?: number;
  receipts: PreparedExpenseReceipt[];
};

export type PreparedExpenseCategory = {
  id: string;
  accountNumber: string;
  name: string;
  sortOrder: number;
  transactions: PreparedExpenseTransaction[];
  subtotal: number;
  totalsIncomplete: boolean;
};

export type PreparedExpenseReport = {
  key: string;
  reference?: string;
  customer?: string;
  project?: string;
  isUnassigned: boolean;
  categories: PreparedExpenseCategory[];
  employeeNames: string[];
  dateFrom: string;
  dateTo: string;
  transactionCount: number;
  total: number;
  totalsIncomplete: boolean;
};

export type ExpenseReportPreparation = {
  reports: PreparedExpenseReport[];
  skipped: {
    notBooked: number;
    earnings: number;
  };
};

type ReportGroup = {
  key: string;
  reference?: string;
  isUnassigned: boolean;
  transactions: TransactionFull[];
};

const cleanText = (value: string) =>
  stringWithoutManualLineBreaks(value).replace(/\s+/g, " ").trim();

export const parseExpenseReference = (reference: string) => {
  const separatorIndex = reference.indexOf(" - ");
  if (separatorIndex < 1) return { reference };

  const customer = reference.slice(0, separatorIndex).trim();
  const project = reference.slice(separatorIndex + 3).trim();
  if (!customer || !project) return { reference };

  return { reference, customer, project };
};

const resolveBaseAmount = (
  transaction: TransactionFull,
  baseCurrency: CurrencyCode
) => {
  if (transaction.currencyCode === baseCurrency) {
    return transaction.baseCurrencyAmount ?? transaction.amount;
  }
  return transaction.baseCurrencyAmount ?? undefined;
};

const roundAmount = (amount: number) => Number(amount.toFixed(2));

const compareText = (left: string, right: string) =>
  left.localeCompare(right, "de-CH", { sensitivity: "base" });

const compareTransactions = (
  left: PreparedExpenseTransaction,
  right: PreparedExpenseTransaction
) =>
  left.paidAt.localeCompare(right.paidAt) ||
  compareText(left.employeeName, right.employeeName) ||
  compareText(left.title, right.title) ||
  left.id.localeCompare(right.id);

const prepareReport = (
  group: ReportGroup,
  baseCurrency: CurrencyCode
): PreparedExpenseReport => {
  const categories = new Map<
    string,
    Omit<PreparedExpenseCategory, "subtotal" | "totalsIncomplete">
  >();

  for (const transaction of group.transactions) {
    const category = categories.get(transaction.bookingAccountId) ?? {
      id: transaction.bookingAccountId,
      accountNumber: transaction.bookingAccount.accountNumber,
      name: cleanText(transaction.bookingAccount.name),
      sortOrder: transaction.bookingAccount.sortOrder,
      transactions: [],
    };
    const attachments = transaction.attachments
      .filter((attachment) => !attachment.deletedAt)
      .sort(
        (left, right) =>
          left.createdAt.localeCompare(right.createdAt) ||
          left.id.localeCompare(right.id)
      );

    category.transactions.push({
      id: transaction.id,
      paidAt: transaction.paidAt,
      title: cleanText(transaction.title),
      employeeName: getUserName(transaction.user, true),
      originalAmount: transaction.amount,
      currencyCode: transaction.currencyCode,
      baseAmount: resolveBaseAmount(transaction, baseCurrency),
      receipts: attachments.map((attachment) => ({
        key: attachment.key,
        id: "",
      })),
    });
    categories.set(transaction.bookingAccountId, category);
  }

  let receiptNumber = 0;
  const categoriesPrepared = [...categories.values()]
    .sort(
      (left, right) =>
        left.sortOrder - right.sortOrder || compareText(left.name, right.name)
    )
    .map((category) => {
      category.transactions.sort(compareTransactions);
      for (const transaction of category.transactions) {
        if (transaction.receipts.length === 0) {
          transaction.receipts.push({ id: "" });
        }
        for (const receipt of transaction.receipts) {
          receiptNumber += 1;
          receipt.id = `B${receiptNumber.toString().padStart(3, "0")}`;
        }
      }

      const completeAmounts = category.transactions
        .map((transaction) => transaction.baseAmount)
        .filter((amount): amount is number => amount !== undefined);
      return {
        ...category,
        subtotal: roundAmount(
          completeAmounts.reduce((sum, amount) => sum + amount, 0)
        ),
        totalsIncomplete:
          completeAmounts.length !== category.transactions.length,
      };
    });

  const employeeNames = [
    ...new Set(
      categoriesPrepared.flatMap((category) =>
        category.transactions.map((transaction) => transaction.employeeName)
      )
    ),
  ].sort(compareText);
  const dates = categoriesPrepared
    .flatMap((category) =>
      category.transactions.map((transaction) => transaction.paidAt)
    )
    .sort();
  const reference = group.reference
    ? parseExpenseReference(group.reference)
    : undefined;

  return {
    key: group.key,
    reference: reference?.reference,
    customer: reference?.customer,
    project: reference?.project,
    isUnassigned: group.isUnassigned,
    categories: categoriesPrepared,
    employeeNames,
    dateFrom: dates[0],
    dateTo: dates[dates.length - 1],
    transactionCount: group.transactions.length,
    total: roundAmount(
      categoriesPrepared.reduce((sum, category) => sum + category.subtotal, 0)
    ),
    totalsIncomplete: categoriesPrepared.some(
      (category) => category.totalsIncomplete
    ),
  };
};

export const prepareExpenseReports = (
  transactions: TransactionFull[],
  baseCurrency: CurrencyCode
): ExpenseReportPreparation => {
  const eligible: TransactionFull[] = [];
  const skipped = { notBooked: 0, earnings: 0 };

  for (const transaction of transactions) {
    if (transaction.status !== TransactionStatus.booked) {
      skipped.notBooked += 1;
    } else if (transaction.bookingAccount.taxType === TaxType.earnings) {
      skipped.earnings += 1;
    } else {
      eligible.push(transaction);
    }
  }

  if (eligible.length === 0) return { reports: [], skipped };

  const hasReferences = eligible.some(
    (transaction) => transaction.transactionReference?.name?.trim()
  );
  if (!hasReferences) {
    return {
      reports: [
        prepareReport(
          {
            key: "all",
            isUnassigned: false,
            transactions: eligible,
          },
          baseCurrency
        ),
      ],
      skipped,
    };
  }

  const groups = new Map<string, ReportGroup>();
  for (const transaction of eligible) {
    const referenceName = transaction.transactionReference?.name
      ? cleanText(transaction.transactionReference.name)
      : "";
    const key = referenceName;
    const group = groups.get(key) ?? {
      key: key || "__unassigned__",
      reference: referenceName || undefined,
      isUnassigned: !referenceName,
      transactions: [],
    };
    group.transactions.push(transaction);
    groups.set(key, group);
  }

  const reports = [...groups.values()]
    .sort((left, right) => {
      if (left.isUnassigned !== right.isUnassigned) {
        return left.isUnassigned ? 1 : -1;
      }
      return compareText(left.reference ?? "", right.reference ?? "");
    })
    .map((group) => prepareReport(group, baseCurrency));

  return { reports, skipped };
};

export const sanitizeExpenseReportFilenamePart = (value: string) =>
  cleanText(value)
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[-_.]+|[-_.]+$/g, "") || "Export";

export const getExpenseReportFilename = (
  report: PreparedExpenseReport,
  organizationName?: string,
  dateFrom = report.dateFrom,
  dateTo = report.dateTo
) => {
  let subject: string | undefined;
  if (report.isUnassigned) {
    subject = "Ohne_Referenz";
  } else if (report.customer && report.project) {
    subject = `${sanitizeExpenseReportFilenamePart(
      report.customer
    )}-${sanitizeExpenseReportFilenamePart(report.project)}`;
  } else if (report.reference) {
    subject = sanitizeExpenseReportFilenamePart(report.reference);
  } else if (organizationName) {
    subject = sanitizeExpenseReportFilenamePart(organizationName);
  }
  return (
    ["Spesenauszug", subject, dateFrom, dateTo].filter(Boolean).join("_") +
    ".pdf"
  );
};

export const getExpenseReportArchiveFilename = (
  organizationName: string | undefined,
  dateFrom: string,
  dateTo: string
) =>
  [
    "Spesenauszüge",
    organizationName
      ? sanitizeExpenseReportFilenamePart(organizationName)
      : undefined,
    dateFrom,
    dateTo,
  ]
    .filter(Boolean)
    .join("_") + ".zip";
